import React, { useEffect, useState } from 'react';
import {
    Box,
    Paper,
    Typography,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Avatar,
    useTheme,
    alpha,
    Button,
    Theme,
    CircularProgress
} from '@mui/material';
import {
    PersonAdd,
    Payment,
    AssignmentTurnedIn,
    EmojiEvents,
    ArrowForward
} from '@mui/icons-material';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { getRecentActivity, ActivityItem } from 'services/dashboardService';

dayjs.extend(relativeTime);

const getActivityIcon = (type: string) => {
    switch (type) {
        case 'signup': return <PersonAdd />;
        case 'payment': return <Payment />;
        case 'submission': return <AssignmentTurnedIn />;
        case 'achievement': return <EmojiEvents />;
        default: return <PersonAdd />;
    }
};

const getActivityColor = (type: string, theme: Theme) => {
    switch (type) {
        case 'signup': return theme.palette.primary.main;
        case 'payment': return theme.palette.success.main;
        case 'submission': return theme.palette.info.main;
        case 'achievement': return theme.palette.warning.main;
        default: return theme.palette.grey[500];
    }
};

const RecentActivity = () => {
    const theme = useTheme();
    const [activities, setActivities] = useState<ActivityItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;

        const fetchActivity = async () => {
            setLoading(true);
            setError(null);
            try {
                const data = await getRecentActivity(10);
                if (!cancelled) {
                    setActivities(data);
                }
            } catch (err) {
                console.error('Error fetching recent activity:', err);
                if (!cancelled) {
                    setError('Unable to load recent activity.');
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        fetchActivity();
        return () => { cancelled = true; };
    }, []);

    const renderBody = () => {
        if (loading) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress size={28} />
                </Box>
            );
        }

        if (error) {
            return (
                <Box sx={{ py: 6, textAlign: 'center' }}>
                    <Typography variant="body2" color="error">
                        {error}
                    </Typography>
                </Box>
            );
        }

        if (activities.length === 0) {
            return (
                <Box sx={{ py: 6, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                        No recent activity yet
                    </Typography>
                </Box>
            );
        }

        return (
            <List sx={{ p: 0 }}>
                {activities.map((activity, index) => (
                    <ListItem
                        key={activity.id}
                        alignItems="flex-start"
                        sx={{
                            px: 0,
                            py: 2,
                            borderBottom: index < activities.length - 1 ? `1px solid ${alpha(theme.palette.divider, 0.5)}` : 'none'
                        }}
                    >
                        <ListItemAvatar>
                            <Avatar
                                sx={{
                                    bgcolor: alpha(getActivityColor(activity.type, theme), 0.1),
                                    color: getActivityColor(activity.type, theme),
                                    borderRadius: 3
                                }}
                            >
                                {getActivityIcon(activity.type)}
                            </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                            primary={
                                <Typography variant="subtitle2" fontWeight="600" color="text.primary">
                                    {activity.title}
                                </Typography>
                            }
                            secondary={
                                <Box component="span" sx={{ display: 'flex', flexDirection: 'column', mt: 0.5 }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ display: 'block' }}>
                                        {activity.description}
                                    </Typography>
                                    <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5 }}>
                                        {dayjs(activity.timestamp).fromNow()}
                                    </Typography>
                                </Box>
                            }
                        />
                    </ListItem>
                ))}
            </List>
        );
    };

    return (
        <Paper
            elevation={0}
            sx={{
                p: 3,
                height: '100%',
                borderRadius: 4,
                bgcolor: 'background.paper',
                border: `1px solid ${theme.palette.divider}`,
            }}
        >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" fontWeight="bold">
                    Recent Activity
                </Typography>
                <Button endIcon={<ArrowForward />} size="small" sx={{ textTransform: 'none', borderRadius: 2 }}>
                    View All
                </Button>
            </Box>

            {renderBody()}
        </Paper>
    );
};

export default RecentActivity;
